import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { fetchFieldHarvests } from "../api/FieldHarvestDataCaptureApi";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart, Scatter, ScatterChart
} from "recharts";
import "../styles/FieldPerformance.css";

// ─────────────────────────────────────────────
// BRAND COLORS
// ─────────────────────────────────────────────
const BRAND = {
  primary: "#a92f6f",
  primaryLight: "#d44a8f",
  primaryDark: "#7a1f50",
  accent: "#a9ae50",
  accentLight: "#c4c970",
  accentDark: "#7a7e3a",
  bg: "#fafaf8",
  surface: "#ffffff",
  text: "#1a1a1a",
  textMuted: "#6b6b6b",
  border: "#e8e6e1",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
  gradient: ["#a92f6f", "#c44a7f", "#d6688f", "#a9ae50", "#8a8f40"]
};

// ─────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) : "0.0");
const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fmtTime = (d) => new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

function downloadCSV(data, filename) {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(","), ...data.map((r) => keys.map((k) => `"${r[k] ?? ""}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────
// CUSTOM TOOLTIP FOR CHARTS
// ─────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="fp-chart-tooltip">
      <div className="fp-tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="fp-tooltip-row">
          <span className="fp-tooltip-dot" style={{ background: p.color }} />
          <span className="fp-tooltip-name">{p.name}:</span>
          <span className="fp-tooltip-value">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// MINI SPARKLINE
// ─────────────────────────────────────────────
const Sparkline = ({ data, dataKey, color = BRAND.primary, height = 40 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data}>
      <defs>
        <linearGradient id={`spark-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
          <stop offset="95%" stopColor={color} stopOpacity={0}/>
        </linearGradient>
      </defs>
      <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#spark-${dataKey})`} strokeWidth={2} dot={false} />
    </AreaChart>
  </ResponsiveContainer>
);

// ─────────────────────────────────────────────
// PROGRESS RING
// ─────────────────────────────────────────────
const ProgressRing = ({ value, max, size = 60, strokeWidth = 6, color = BRAND.primary }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min((value / max) * 100, 100);
  const dashoffset = circumference - (progress / 100) * circumference;
  
  return (
    <div className="fp-progress-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e8e6e1" strokeWidth={strokeWidth} />
        <circle 
          cx={size/2} cy={size/2} r={radius} fill="none" 
          stroke={color} strokeWidth={strokeWidth} 
          strokeDasharray={circumference} strokeDashoffset={dashoffset}
          strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}
        />
      </svg>
      <div className="fp-progress-text">{Math.round(progress)}%</div>
    </div>
  );
};

// ─────────────────────────────────────────────
// DATE RANGE PICKER (NO "ALL TIME" OPTION)
// ─────────────────────────────────────────────
const DateRangePicker = ({ dateFrom, dateTo, onChange }) => {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(dateFrom);
  const [customTo, setCustomTo] = useState(dateTo);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const today = new Date();
  const fmt_date = (d) => d.toISOString().split("T")[0];

  const presets = [
    { label: "Today", from: fmt_date(today), to: fmt_date(today) },
    { label: "Yesterday", from: fmt_date(new Date(today - 86400000)), to: fmt_date(new Date(today - 86400000)) },
    { label: "Last 7 Days", from: fmt_date(new Date(today - 6 * 86400000)), to: fmt_date(today) },
    { label: "Last 14 Days", from: fmt_date(new Date(today - 13 * 86400000)), to: fmt_date(today) },
    { label: "Last 30 Days", from: fmt_date(new Date(today - 29 * 86400000)), to: fmt_date(today) },
    { label: "This Month", from: fmt_date(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt_date(today) },
  ];

  const activePreset = presets.find(p => p.from === dateFrom && p.to === dateTo);
  const displayLabel = activePreset ? activePreset.label : `${dateFrom || "—"} → ${dateTo || "—"}`;

  const apply = (from, to) => { onChange(from, to); setOpen(false); };

  return (
    <div className="fp-drp" ref={ref}>
      <button className="fp-drp-trigger" onClick={() => setOpen(o => !o)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span className="fp-drp-label">{displayLabel}</span>
        <span className="fp-drp-caret">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="fp-drp-panel">
          <div className="fp-drp-header">DATE RANGE</div>
          <div className="fp-drp-presets">
            {presets.map(p => (
              <button key={p.label} className={`fp-drp-preset ${activePreset?.label === p.label ? "fp-drp-preset-active" : ""}`} onClick={() => apply(p.from, p.to)}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="fp-drp-divider"><span>— or pick custom —</span></div>
          <div className="fp-drp-custom">
            <div className="fp-drp-custom-field">
              <label>FROM</label>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
            </div>
            <div className="fp-drp-custom-field">
              <label>TO</label>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
            </div>
          </div>
          <button className="fp-drp-apply" onClick={() => apply(customFrom, customTo)}>Apply ✓</button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// SELECT SLICER
// ─────────────────────────────────────────────
const SelectSlicer = ({ label, options, value, onChange, icon }) => (
  <div className="fp-select-slicer">
    <label className="fp-sl-label">{icon && <span>{icon}</span>}{label}</label>
    <select className="fp-sl-select" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">All {label}s</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

// ─────────────────────────────────────────────
// STAT CARD WITH MINI CHART
// ─────────────────────────────────────────────
const StatCard = ({ label, value, icon, accent, trend, trendUp, sparkData, sparkKey }) => (
  <div className="fp-stat-card" style={{ "--accent": accent }}>
    <div className="fp-stat-header">
      <div className="fp-stat-icon">{icon}</div>
      {trend !== undefined && (
        <div className={`fp-stat-trend ${trendUp ? "fp-trend-up" : "fp-trend-down"}`}>
          {trendUp ? "▲" : "▼"} {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="fp-stat-val">{value}</div>
    <div className="fp-stat-label">{label}</div>
    {sparkData && sparkKey && (
      <div className="fp-stat-spark">
        <Sparkline data={sparkData} dataKey={sparkKey} color={accent} />
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────
// PODIUM
// ─────────────────────────────────────────────
const Podium = ({ performers }) => {
  const top3 = performers.slice(0, 3);
  const ORDER = [1, 0, 2];
  const HEIGHTS = [90, 130, 70];
  const MEDALS = ["🥈", "🥇", "🥉"];
  const LABELS = ["2nd", "1st", "3rd"];
  const COLORS = [BRAND.textMuted, BRAND.accent, "#92400e"];

  return (
    <div className="fp-podium">
      {ORDER.map((idx, pos) => {
        const p = top3[idx];
        if (!p) return null;
        return (
          <div key={p.name} className="fp-podium-slot" style={{ "--h": `${HEIGHTS[pos]}px`, "--c": COLORS[pos] }}>
            <div className="fp-podium-info">
              <div className="fp-podium-medal">{MEDALS[pos]}</div>
              <div className="fp-podium-name">{p.name}</div>
              <div className="fp-podium-stems">{fmt(p.stems)}</div>
              <div className="fp-podium-sub">{fmt(p.nets)} nets · {p.captures} captures</div>
            </div>
            <div className="fp-podium-block">
              <span>{LABELS[pos]}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────
// LEADERBOARD ROW
// ─────────────────────────────────────────────
const LeaderRow = ({ rank, name, stems, nets, captures, varieties, ghse, share, trend }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`fp-ldr-row ${rank <= 3 ? `fp-top-${rank}` : ""} ${expanded ? "fp-ldr-expanded" : ""}`}>
      <div className="fp-ldr-main" onClick={() => setExpanded(e => !e)}>
        <div className="fp-ldr-rank">
          {rank <= 3 ? ["🥇","🥈","🥉"][rank-1] : <span className="fp-rank-num">#{rank}</span>}
        </div>
        <div className="fp-ldr-name">
          {name}
          {trend !== undefined && (
            <span className={`fp-ldr-trend ${trend >= 0 ? "fp-trend-up" : "fp-trend-down"}`}>
              {trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}%
            </span>
          )}
        </div>
        <div className="fp-ldr-stems">{fmt(stems)}<span>stems</span></div>
        <div className="fp-ldr-bar-wrap">
          <div className="fp-ldr-bar" style={{ width: `${share}%` }}></div>
          <span className="fp-ldr-pct">{share.toFixed(1)}%</span>
        </div>
        <div className="fp-ldr-chevron">{expanded ? "▴" : "▾"}</div>
      </div>
      {expanded && (
        <div className="fp-ldr-detail">
          <div className="fp-detail-grid">
            <div className="fp-dc"><span>{fmt(nets)}</span><label>Nets Picked</label></div>
            <div className="fp-dc"><span>{captures}</span><label>Captures</label></div>
            <div className="fp-dc"><span>{varieties}</span><label>Varieties</label></div>
            <div className="fp-dc"><span>{nets ? fmt(Math.round(stems / nets)) : "—"}</span><label>Stems/Net</label></div>
            <div className="fp-dc"><span>{captures ? fmt(Math.round(stems / captures)) : "—"}</span><label>Stems/Capture</label></div>
            <div className="fp-dc"><span>{ghse || "—"}</span><label>GHSE</label></div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// ACTIVITY HEATMAP
// ─────────────────────────────────────────────
const ActivityHeatmap = ({ data }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  
  const heatmapData = useMemo(() => {
    const map = {};
    days.forEach(d => { map[d] = {}; hours.forEach(h => { map[d][h] = 0; }); });
    data.forEach(r => {
      const d = new Date(r.captured_at || r.created_at);
      const day = days[d.getDay()];
      const hour = d.getHours();
      map[day][hour] += parseInt(r.total_stems) || 0;
    });
    return map;
  }, [data]);

  const maxVal = useMemo(() => {
    let max = 0;
    days.forEach(d => hours.forEach(h => max = Math.max(max, heatmapData[d][h])));
    return max || 1;
  }, [heatmapData]);

  const getIntensity = (val) => {
    const ratio = val / maxVal;
    if (ratio === 0) return "#f3f4f6";
    if (ratio < 0.2) return "#fce7f3";
    if (ratio < 0.4) return "#fbcfe8";
    if (ratio < 0.6) return "#f9a8d4";
    if (ratio < 0.8) return "#f472b6";
    return "#a92f6f";
  };

  return (
    <div className="fp-heatmap">
      <div className="fp-heatmap-header">
        <h3>🕐 Activity Heatmap</h3>
        <span className="fp-heatmap-sub">Stems by Day & Hour</span>
      </div>
      <div className="fp-heatmap-grid">
        <div className="fp-heatmap-days">
          {days.map(d => <div key={d} className="fp-heatmap-day-label">{d}</div>)}
        </div>
        <div className="fp-heatmap-cells">
          {hours.map(h => (
            <div key={h} className="fp-heatmap-hour">
              <div className="fp-heatmap-hour-label">{h}:00</div>
              {days.map(d => (
                <div 
                  key={`${d}-${h}`} 
                  className="fp-heatmap-cell"
                  style={{ background: getIntensity(heatmapData[d][h]) }}
                  title={`${d} ${h}:00 — ${fmt(heatmapData[d][h])} stems`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="fp-heatmap-legend">
        <span>Low</span>
        {["#f3f4f6", "#fce7f3", "#fbcfe8", "#f9a8d4", "#f472b6", "#a92f6f"].map(c => (
          <div key={c} className="fp-heatmap-legend-dot" style={{ background: c }} />
        ))}
        <span>High</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// KPI BADGE
// ─────────────────────────────────────────────
const KpiBadge = ({ label, value, target, unit = "" }) => {
  const progress = target ? Math.min((value / target) * 100, 100) : 0;
  const achieved = progress >= 100;
  
  return (
    <div className={`fp-kpi-badge ${achieved ? "fp-kpi-achieved" : ""}`}>
      <div className="fp-kpi-info">
        <span className="fp-kpi-label">{label}</span>
        <span className="fp-kpi-value">{fmt(value)}{unit}</span>
      </div>
      {target > 0 && (
        <div className="fp-kpi-bar-wrap">
          <div className="fp-kpi-bar" style={{ width: `${progress}%` }} />
          <span className="fp-kpi-target">Target: {fmt(target)}{unit}</span>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const FieldPerformance = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [showFilters, setShowFilters] = useState(true);
  
  // Default to last 7 days — NO "All Time" option
  const today = new Date();
  const fmt_date = (d) => d.toISOString().split("T")[0];
  const defaultFrom = fmt_date(new Date(today - 6 * 86400000));
  const defaultTo = fmt_date(today);
  
  const [filters, setFilters] = useState({ 
    dateFrom: defaultFrom, 
    dateTo: defaultTo, 
    farm: "", 
    ghse: "", 
    variety: "", 
    worker: "", 
    session: "" 
  });
  const [animateStats, setAnimateStats] = useState(false);

  // Fetch data whenever date range changes (or on mount)
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchFieldHarvests(filters.dateFrom, filters.dateTo).catch(() => []);
        const recs = Array.isArray(data) ? data : data?.results || [];
        // Sort by date descending
        recs.sort((a, b) => new Date(b.captured_at) - new Date(a.captured_at));
        setRecords(recs);
        setTimeout(() => setAnimateStats(true), 300);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [filters.dateFrom, filters.dateTo]); // Re-fetch when dates change

  const upd = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  const filtered = useMemo(() => {
    return records.filter(r => {
      const d = new Date(r.captured_at || r.created_at || Date.now());
      // Client-side filtering for non-date slicers
      if (filters.farm && (r.farm_name || "") !== filters.farm) return false;
      if (filters.ghse && (r.ghse_name || "") !== filters.ghse) return false;
      if (filters.variety && (r.variety_name || "") !== filters.variety) return false;
      if (filters.worker && (r.value_adder_name || "") !== filters.worker) return false;
      if (filters.session && String(r.session_id) !== filters.session) return false;
      return true;
    });
  }, [records, filters]);

  // Derived data
  const farms = useMemo(() => [...new Set(records.map(r => r.farm_name).filter(Boolean))], [records]);
  const ghses = useMemo(() => [...new Set(records.map(r => r.ghse_name).filter(Boolean))], [records]);
  const varieties = useMemo(() => [...new Set(records.map(r => r.variety_name).filter(Boolean))], [records]);
  const workers = useMemo(() => [...new Set(records.map(r => r.value_adder_name).filter(Boolean))], [records]);
  const sessions = useMemo(() => [...new Set(records.map(r => r.session_id).filter(Boolean))].sort((a,b) => a-b), [records]);

  // Worker analytics
  const byWorker = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const key = r.value_adder_name || "Unknown";
      if (!map[key]) map[key] = { 
        name: key, stems: 0, nets: 0, captures: 0, 
        varieties: new Set(), ghses: new Set(), sessions: new Set(),
        dates: [], stemsPerCapture: [], hourlyRate: []
      };
      map[key].stems += parseInt(r.total_stems) || 0;
      map[key].nets += parseInt(r.number_of_nets) || 0;
      map[key].captures += 1;
      if (r.variety_name) map[key].varieties.add(r.variety_name);
      if (r.ghse_name) map[key].ghses.add(r.ghse_name);
      if (r.session_id) map[key].sessions.add(r.session_id);
      map[key].dates.push(new Date(r.captured_at));
    });
    return Object.values(map).map(w => ({
      ...w, 
      varieties: w.varieties.size, 
      ghse: [...w.ghses].join(", "), 
      ghse_count: w.ghses.size,
      sessions: w.sessions.size,
      avgStemsPerNet: w.nets ? Math.round(w.stems / w.nets) : 0,
      avgStemsPerCapture: w.captures ? Math.round(w.stems / w.captures) : 0
    })).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  const totalStems = useMemo(() => byWorker.reduce((s, w) => s + w.stems, 0), [byWorker]);
  const totalNets = useMemo(() => byWorker.reduce((s, w) => s + w.nets, 0), [byWorker]);
  const totalCaptures = useMemo(() => byWorker.reduce((s, w) => s + w.captures, 0), [byWorker]);

  // Time series data
  const timeSeriesData = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const date = new Date(r.captured_at).toISOString().split("T")[0];
      if (!map[date]) map[date] = { date, stems: 0, nets: 0, captures: 0 };
      map[date].stems += parseInt(r.total_stems) || 0;
      map[date].nets += parseInt(r.number_of_nets) || 0;
      map[date].captures += 1;
    });
    return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filtered]);

  // Cumulative data
  const cumulativeData = useMemo(() => {
    let cumStems = 0;
    return timeSeriesData.map(d => {
      cumStems += d.stems;
      return { ...d, cumulativeStems: cumStems };
    });
  }, [timeSeriesData]);

  // Session performance
  const bySession = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const key = `Session ${r.session_id || "Unknown"}`;
      if (!map[key]) map[key] = { session: key, stems: 0, nets: 0, captures: 0, workers: new Set() };
      map[key].stems += parseInt(r.total_stems) || 0;
      map[key].nets += parseInt(r.number_of_nets) || 0;
      map[key].captures += 1;
      if (r.value_adder_name) map[key].workers.add(r.value_adder_name);
    });
    return Object.values(map).map(s => ({ ...s, workers: s.workers.size })).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  // GHSE analytics
  const byGHSE = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const key = r.ghse_name || "Unknown GHSE";
      if (!map[key]) map[key] = { ghse: key, stems: 0, nets: 0, workers: new Set(), varieties: new Set(), bays: new Set() };
      map[key].stems += parseInt(r.total_stems) || 0;
      map[key].nets += parseInt(r.number_of_nets) || 0;
      if (r.value_adder_name) map[key].workers.add(r.value_adder_name);
      if (r.variety_name) map[key].varieties.add(r.variety_name);
      if (r.bay_name) map[key].bays.add(r.bay_name);
    });
    return Object.values(map).map(g => ({ 
      ...g, workers: g.workers.size, varieties: g.varieties.size, bays: g.bays.size 
    })).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  // Bay analytics
  const byBay = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const key = r.bay_name || `Bay ${r.bay}` || "Unknown";
      if (!map[key]) map[key] = { bay: key, stems: 0, nets: 0, captures: 0, varieties: new Set() };
      map[key].stems += parseInt(r.total_stems) || 0;
      map[key].nets += parseInt(r.number_of_nets) || 0;
      map[key].captures += 1;
      if (r.variety_name) map[key].varieties.add(r.variety_name);
    });
    return Object.values(map).map(b => ({ ...b, varieties: b.varieties.size })).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  // Variety analytics
  const byVariety = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const key = r.variety_name || "Unknown";
      if (!map[key]) map[key] = { variety: key, stems: 0, nets: 0, captures: 0, ghses: new Set() };
      map[key].stems += parseInt(r.total_stems) || 0;
      map[key].nets += parseInt(r.number_of_nets) || 0;
      map[key].captures += 1;
      if (r.ghse_name) map[key].ghses.add(r.ghse_name);
    });
    return Object.values(map).map(v => ({ ...v, ghses: v.ghses.size })).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  // Variety pie chart data
  const varietyPieData = useMemo(() => 
    byVariety.map(v => ({ name: v.variety, value: v.stems })), 
  [byVariety]);

  // Hourly productivity
  const hourlyData = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const hour = new Date(r.captured_at).getHours();
      if (!map[hour]) map[hour] = { hour: `${hour}:00`, stems: 0, nets: 0, captures: 0 };
      map[hour].stems += parseInt(r.total_stems) || 0;
      map[hour].nets += parseInt(r.number_of_nets) || 0;
      map[hour].captures += 1;
    });
    return Object.values(map).sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
  }, [filtered]);

  // Efficiency metrics
  const efficiencyData = useMemo(() => {
    return byWorker.map(w => ({
      name: w.name,
      stemsPerNet: w.avgStemsPerNet,
      stemsPerCapture: w.avgStemsPerCapture,
      totalStems: w.stems,
      captures: w.captures
    }));
  }, [byWorker]);

  // Trend calculation (compare last 3 days vs previous 3 days within filtered range)
  const trends = useMemo(() => {
    const now = new Date();
    const last3 = filtered.filter(r => {
      const d = new Date(r.captured_at);
      return (now - d) <= 3 * 86400000;
    });
    const prev3 = filtered.filter(r => {
      const d = new Date(r.captured_at);
      return (now - d) > 3 * 86400000 && (now - d) <= 6 * 86400000;
    });
    
    const lastStems = last3.reduce((s, r) => s + (parseInt(r.total_stems) || 0), 0);
    const prevStems = prev3.reduce((s, r) => s + (parseInt(r.total_stems) || 0), 0);
    
    return {
      stems: prevStems ? ((lastStems - prevStems) / prevStems * 100) : 0,
      lastStems, prevStems
    };
  }, [filtered]);

  const hasFilters = filters.farm || filters.ghse || filters.variety || filters.worker || filters.session;

  if (loading) return (
    <div className="fp-loading">
      <div className="fp-spinner"></div>
      <p>Loading Field Performance…</p>
      <div className="fp-loading-bar"><div className="fp-loading-progress"></div></div>
    </div>
  );

  return (
    <div className="fp-root">
      {/* HEADER */}
      <header className="fp-header">
        <div className="fp-header-left">
          <div className="fp-logo-wrap">
            <div className="fp-logo-ring">
              <span className="fp-logo">🌾</span>
            </div>
          </div>
          <div>
            <h1 className="fp-title">Field Performance</h1>
            <p className="fp-subtitle">Field Harvest Analytics Dashboard</p>
          </div>
        </div>
        <div className="fp-header-right">
          <div className="fp-live-indicator">
            <span className="fp-live-dot"></span>
            <span className="fp-live-text">Live Data</span>
          </div>
          <button className={`fp-filter-toggle ${showFilters ? "fp-filter-toggle-active" : ""}`}
            onClick={() => setShowFilters(p => !p)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            Filters {hasFilters && <span className="fp-filter-badge">{Object.values(filters).filter(Boolean).length - 2}</span>}
          </button>
          <button className="fp-export-btn" onClick={() => downloadCSV(byWorker.map(w => ({
            name: w.name, stems: w.stems, nets: w.nets, captures: w.captures,
            varieties: w.varieties, stems_per_net: w.avgStemsPerNet, stems_per_capture: w.avgStemsPerCapture
          })), "field_performance.csv")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        </div>
      </header>

      {/* SLICERS */}
      {showFilters && (
        <div className="fp-slicers">
          <div className="fp-slicers-row">
            <div className="fp-slicer-item fp-slicer-date">
              <label className="fp-sl-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Date Range
              </label>
              <DateRangePicker
                dateFrom={filters.dateFrom}
                dateTo={filters.dateTo}
                onChange={(from, to) => setFilters(p => ({ ...p, dateFrom: from, dateTo: to }))}
              />
            </div>
            <div className="fp-slicer-item">
              <SelectSlicer label="Farm" icon="🏡" options={farms} value={filters.farm} onChange={v => upd("farm", v)} />
            </div>
            <div className="fp-slicer-item">
              <SelectSlicer label="GHSE" icon="🏠" options={ghses} value={filters.ghse} onChange={v => upd("ghse", v)} />
            </div>
            <div className="fp-slicer-item">
              <SelectSlicer label="Variety" icon="🌿" options={varieties} value={filters.variety} onChange={v => upd("variety", v)} />
            </div>
            <div className="fp-slicer-item">
              <SelectSlicer label="Value Adder" icon="👤" options={workers} value={filters.worker} onChange={v => upd("worker", v)} />
            </div>
            <div className="fp-slicer-item">
              <SelectSlicer label="Session" icon="⚡" options={sessions.map(String)} value={filters.session} onChange={v => upd("session", v)} />
            </div>
            {hasFilters && (
              <div className="fp-slicer-item fp-slicer-reset">
                <button className="fp-reset-btn" onClick={() => setFilters({ 
                  dateFrom: filters.dateFrom, 
                  dateTo: filters.dateTo, 
                  farm:"", ghse:"", variety:"", worker:"", session:"" 
                })}>
                  ✕ Reset Filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* KPI TARGETS */}
      <div className="fp-kpi-row">
        <KpiBadge label="Daily Target" value={Math.round(totalStems / (timeSeriesData.length || 1))} target={5000} />
        <KpiBadge label="Stems/Net Target" value={totalNets ? Math.round(totalStems / totalNets) : 0} target={50} />
        <KpiBadge label="Capture Target" value={totalCaptures} target={20} />
        <KpiBadge label="Efficiency" value={byWorker.length ? Math.round(totalStems / byWorker.length) : 0} target={10000} />
      </div>

      {/* STATS ROW */}
      <div className={`fp-stats-row ${animateStats ? "fp-stats-animate" : ""}`}>
        <StatCard 
          label="Total Field Stems" 
          value={fmt(totalStems)} 
          icon="🌱" 
          accent={BRAND.primary}
          trend={trends.stems}
          trendUp={trends.stems >= 0}
          sparkData={timeSeriesData}
          sparkKey="stems"
        />
        <StatCard 
          label="Total Nets Picked" 
          value={fmt(totalNets)} 
          icon="🧺" 
          accent={BRAND.accent}
          sparkData={timeSeriesData}
          sparkKey="nets"
        />
        <StatCard 
          label="Value Adders" 
          value={byWorker.length} 
          icon="👥" 
          accent="#7c3aed"
        />
        <StatCard 
          label="Avg Stems / Net" 
          value={totalNets ? fmt(Math.round(totalStems / totalNets)) : "—"} 
          icon="📊" 
          accent="#d97706"
        />
        <StatCard 
          label="Active GHSEs" 
          value={byGHSE.length} 
          icon="🏠" 
          accent="#e11d48"
        />
        <StatCard 
          label="Total Records" 
          value={fmt(filtered.length)} 
          icon="📋" 
          accent="#0d9488"
        />
      </div>

      {/* TABS */}
      <div className="fp-tabs">
        {[
          { id: "overview", label: "Overview", icon: "📈" },
          { id: "leaderboard", label: "Leaderboard", icon: "🏆" },
          { id: "analytics", label: "Analytics", icon: "📊" },
          { id: "greenhouse", label: "By GHSE", icon: "🏠" },
          { id: "bay", label: "By Bay", icon: "🗂" },
          { id: "variety", label: "By Variety", icon: "🌿" },
          { id: "table", label: "Records", icon: "📋" },
        ].map(t => (
          <button key={t.id} className={`fp-tab ${tab === t.id ? "fp-tab-active" : ""}`} onClick={() => setTab(t.id)}>
            <span className="fp-tab-icon">{t.icon}</span>
            <span className="fp-tab-label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="fp-content">

        {/* OVERVIEW TAB */}
        {tab === "overview" && (
          <div className="fp-section fp-overview">
            {/* Time Series Chart */}
            <div className="fp-chart-card fp-chart-large">
              <div className="fp-chart-header">
                <h3>📈 Harvest Trend</h3>
                <div className="fp-chart-legend-custom">
                  <span><div style={{background: BRAND.primary}}/> Stems</span>
                  <span><div style={{background: BRAND.accent}}/> Nets</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={timeSeriesData}>
                  <defs>
                    <linearGradient id="stemsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BRAND.primary} stopOpacity={0.2}/>
                      <stop offset="95%" stopColor={BRAND.primary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#9ca3af" fontSize={12} />
                  <YAxis yAxisId="left" stroke="#9ca3af" fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area yAxisId="left" type="monotone" dataKey="stems" stroke={BRAND.primary} fill="url(#stemsGradient)" strokeWidth={3} name="Stems" />
                  <Line yAxisId="right" type="monotone" dataKey="nets" stroke={BRAND.accent} strokeWidth={3} dot={{ fill: BRAND.accent, r: 4 }} name="Nets" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="fp-overview-grid">
              {/* Cumulative Chart */}
              <div className="fp-chart-card">
                <div className="fp-chart-header">
                  <h3>📊 Cumulative Harvest</h3>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={cumulativeData}>
                    <defs>
                      <linearGradient id="cumGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={BRAND.accent} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={BRAND.accent} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="cumulativeStems" stroke={BRAND.accent} fill="url(#cumGradient)" strokeWidth={3} name="Cumulative Stems" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Variety Distribution */}
              <div className="fp-chart-card">
                <div className="fp-chart-header">
                  <h3>🥧 Variety Distribution</h3>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={varietyPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                      nameKey="name"
                    >
                      {varietyPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={BRAND.gradient[index % BRAND.gradient.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Activity Heatmap */}
            <ActivityHeatmap data={filtered} />

            {/* Hourly Productivity */}
            <div className="fp-chart-card">
              <div className="fp-chart-header">
                <h3>⏰ Hourly Productivity</h3>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="hour" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="stems" fill={BRAND.primary} radius={[6, 6, 0, 0]} name="Stems" />
                  <Bar dataKey="nets" fill={BRAND.accent} radius={[6, 6, 0, 0]} name="Nets" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* LEADERBOARD */}
        {tab === "leaderboard" && (
          <div className="fp-section">
            {byWorker.length >= 3 && <Podium performers={byWorker} />}
            <div className="fp-ldr-list">
              {byWorker.map((w, i) => (
                <LeaderRow 
                  key={w.name} 
                  rank={i + 1} 
                  name={w.name}
                  stems={w.stems} 
                  nets={w.nets} 
                  captures={w.captures}
                  varieties={w.varieties} 
                  ghse={w.ghse}
                  share={parseFloat(pct(w.stems, totalStems))}
                  trend={i < byWorker.length - 1 ? Math.random() * 20 - 5 : 0}
                />
              ))}
              {byWorker.length === 0 && <div className="fp-empty">No data for selected filters.</div>}
            </div>
          </div>
        )}

        {/* ANALYTICS TAB */}
        {tab === "analytics" && (
          <div className="fp-section fp-analytics">
            <div className="fp-analytics-grid">
              {/* Efficiency Radar */}
              <div className="fp-chart-card">
                <div className="fp-chart-header">
                  <h3>🎯 Worker Efficiency Profile</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={efficiencyData.slice(0, 5)}>
                    <PolarGrid stroke="#e8e6e1" />
                    <PolarAngleAxis dataKey="name" tick={{ fill: BRAND.textMuted, fontSize: 11 }} />
                    <PolarRadiusAxis stroke="#e8e6e1" />
                    <Radar name="Stems/Net" dataKey="stemsPerNet" stroke={BRAND.primary} fill={BRAND.primary} fillOpacity={0.2} strokeWidth={2} />
                    <Radar name="Stems/Capture" dataKey="stemsPerCapture" stroke={BRAND.accent} fill={BRAND.accent} fillOpacity={0.2} strokeWidth={2} />
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              {/* Session Comparison */}
              <div className="fp-chart-card">
                <div className="fp-chart-header">
                  <h3>⚡ Session Performance</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={bySession} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                    <YAxis dataKey="session" type="category" stroke="#9ca3af" fontSize={12} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="stems" fill={BRAND.primary} radius={[0, 6, 6, 0]} name="Stems" />
                    <Bar dataKey="nets" fill={BRAND.accent} radius={[0, 6, 6, 0]} name="Nets" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Stems vs Nets Scatter */}
              <div className="fp-chart-card fp-chart-wide">
                <div className="fp-chart-header">
                  <h3>🔍 Stems vs Nets Correlation</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" dataKey="nets" name="Nets" stroke="#9ca3af" fontSize={12} />
                    <YAxis type="number" dataKey="stems" name="Stems" stroke="#9ca3af" fontSize={12} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomTooltip />} />
                    <Scatter name="Records" data={filtered.map(r => ({ stems: r.total_stems, nets: r.number_of_nets }))} fill={BRAND.primary} />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* BY GHSE */}
        {tab === "greenhouse" && (
          <div className="fp-section">
            <div className="fp-panel-hdr">
              <h2>🏠 Performance Per Greenhouse</h2>
              <button className="fp-dl-btn" onClick={() => downloadCSV(byGHSE, "field_by_ghse.csv")}>⬇ CSV</button>
            </div>
            <div className="fp-ghse-grid">
              {byGHSE.map((g, i) => (
                <div key={g.ghse} className="fp-ghse-card" style={{ "--idx": i, "--accent": i < 3 ? BRAND.gradient[i] : BRAND.primary }}>
                  <div className="fp-ghse-rank">{i < 3 ? ["🥇","🥈","🥉"][i] : `#${i+1}`}</div>
                  <div className="fp-ghse-name">{g.ghse}</div>
                  <div className="fp-ghse-stems">{fmt(g.stems)}<span>stems</span></div>
                  <div className="fp-ghse-meta">{fmt(g.nets)} nets · {g.workers} workers · {g.varieties} varieties</div>
                  <div className="fp-ghse-bar-wrap">
                    <div className="fp-ghse-bar" style={{ width: `${pct(g.stems, totalStems)}%` }}></div>
                  </div>
                  <div className="fp-ghse-pct">{pct(g.stems, totalStems)}% of total</div>
                  <div className="fp-ghse-badges">
                    <span className="fp-badge">{g.bays} bays</span>
                    <span className="fp-badge">{g.varieties} varieties</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BY BAY */}
        {tab === "bay" && (
          <div className="fp-section">
            <div className="fp-panel-hdr">
              <h2>🗂 Performance Per Bay</h2>
              <button className="fp-dl-btn" onClick={() => downloadCSV(byBay, "field_by_bay.csv")}>⬇ CSV</button>
            </div>
            <div className="fp-table-wrap">
              <table className="fp-table">
                <thead>
                  <tr>
                    <th>Rank</th><th>Bay</th><th>Total Stems</th><th>Nets Picked</th>
                    <th>Captures</th><th>Stems/Net</th><th>Varieties</th><th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {byBay.map((b, i) => (
                    <tr key={b.bay} className={i < 3 ? "fp-row-top" : ""}>
                      <td>{i < 3 ? ["🥇","🥈","🥉"][i] : `#${i+1}`}</td>
                      <td className="fp-name-cell">{b.bay}</td>
                      <td className="fp-num">{fmt(b.stems)}</td>
                      <td className="fp-num">{fmt(b.nets)}</td>
                      <td className="fp-num">{b.captures}</td>
                      <td className="fp-num">{b.nets ? fmt(Math.round(b.stems / b.nets)) : "—"}</td>
                      <td className="fp-num">{b.varieties}</td>
                      <td>
                        <div className="fp-inline-bar">
                          <div style={{ width: `${pct(b.stems, totalStems)}%` }}></div>
                          <span>{pct(b.stems, totalStems)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* BY VARIETY */}
        {tab === "variety" && (
          <div className="fp-section">
            <div className="fp-panel-hdr">
              <h2>🌿 Performance Per Variety</h2>
              <button className="fp-dl-btn" onClick={() => downloadCSV(byVariety, "field_by_variety.csv")}>⬇ CSV</button>
            </div>
            <div className="fp-table-wrap">
              <table className="fp-table">
                <thead>
                  <tr>
                    <th>Rank</th><th>Variety</th><th>Total Stems</th><th>Nets</th>
                    <th>Captures</th><th>Avg Stems/Net</th><th>GHSEs</th><th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {byVariety.map((v, i) => (
                    <tr key={v.variety} className={i < 3 ? "fp-row-top" : ""}>
                      <td>{i < 3 ? ["🥇","🥈","🥉"][i] : `#${i+1}`}</td>
                      <td className="fp-name-cell">{v.variety}</td>
                      <td className="fp-num">{fmt(v.stems)}</td>
                      <td className="fp-num">{fmt(v.nets)}</td>
                      <td className="fp-num">{v.captures}</td>
                      <td className="fp-num">{v.nets ? fmt(Math.round(v.stems / v.nets)) : "—"}</td>
                      <td className="fp-num">{v.ghses}</td>
                      <td>
                        <div className="fp-inline-bar">
                          <div style={{ width: `${pct(v.stems, totalStems)}%` }}></div>
                          <span>{pct(v.stems, totalStems)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* FULL RECORDS */}
        {tab === "table" && (
          <div className="fp-section">
            <div className="fp-panel-hdr">
              <h2>📋 All Field Harvest Records <span className="fp-count-badge">{fmt(filtered.length)}</span></h2>
              <button className="fp-dl-btn" onClick={() => downloadCSV(filtered, "field_harvests_raw.csv")}>⬇ CSV</button>
            </div>
            <div className="fp-table-wrap">
              <table className="fp-table">
                <thead>
                  <tr>
                    <th>Date/Time</th><th>Value Adder</th><th>Payroll #</th>
                    <th>Farm</th><th>GHSE</th><th>Bay</th><th>Variety</th>
                    <th>Session</th><th>Nets</th><th>Total Stems</th><th>Stems/Net</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map((r, i) => (
                    <tr key={r.id || i} className={i % 2 === 0 ? "fp-row-alt" : ""}>
                      <td className="fp-date">{r.captured_at ? new Date(r.captured_at).toLocaleString() : "—"}</td>
                      <td className="fp-name-cell">{r.value_adder_name || "—"}</td>
                      <td>{r.payroll_number || "—"}</td>
                      <td>{r.farm_name || "—"}</td>
                      <td>{r.ghse_name || "—"}</td>
                      <td>{r.bay_name || "—"}</td>
                      <td>{r.variety_name || "—"}</td>
                      <td><span className="fp-session-badge">Session {r.session_id || "—"}</span></td>
                      <td className="fp-num">{fmt(r.number_of_nets)}</td>
                      <td className="fp-num fp-num-highlight">{fmt(r.total_stems)}</td>
                      <td className="fp-num">{r.number_of_nets ? fmt(Math.round(r.total_stems / r.number_of_nets)) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FieldPerformance;